import './health-professional-list-item.scss';

import { kebabCase } from 'lodash';
import { useMemo } from 'react';

import {
  HealthProfessionalItemDataFragment,
  HealthProfessionalStatus,
} from '../../../data/types/api/graphql';
import { useFormatters } from '../../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../../data/utils/make-key-down-handler';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { Tag, TagType } from '../../../elements/tag/tag';

export enum HealthProfessionalListItemType {
  DEFAULT = 'default',
  ACTIVE = 'active',
  NOTACTIVE = 'not-active',
}

type HealthProfessionalListItemProps = {
  type?: HealthProfessionalListItemType;
  healthProfessional: HealthProfessionalItemDataFragment;
  onClick?: (value: HealthProfessionalItemDataFragment) => void;
};

export function HealthProfessionalListItem({
  healthProfessional,
  onClick,
  ...props
}: HealthProfessionalListItemProps) {
  const type = useMemo(() => {
    if (props.type) {
      return props.type;
    }

    if (healthProfessional.status === HealthProfessionalStatus.Active) {
      return HealthProfessionalListItemType.ACTIVE;
    }

    if (healthProfessional.status === HealthProfessionalStatus.Inactive) {
      return HealthProfessionalListItemType.NOTACTIVE;
    }

    return HealthProfessionalListItemType.DEFAULT;
  }, [props.type, healthProfessional]);

  const attributes = useMemo(() => {
    if (onClick) {
      return {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick(healthProfessional),
        onKeyDown: makeKeyDownHandler(() => onClick(healthProfessional)),
      };
    }

    return {};
  }, [healthProfessional, onClick]);

  const { getFullName } = useFormatters();

  return (
    <div
      {...attributes}
      className={`health-professional-list-item health-professional-list-item--${type}`}
    >
      <div className="health-professional-list-item__content">
        <h3>{getFullName(healthProfessional)}</h3>
        <p>
          <CustomFormattedMessage
            id={`common.honorific.${kebabCase(healthProfessional.honorific)}`}
          />
        </p>
      </div>
      <div className="health-professional-list-item__content">
        {healthProfessional.healthProvider?.name && (
          <p>{healthProfessional.healthProvider?.name}</p>
        )}
      </div>
      <div className="health-professional-list-item__tags">
        {healthProfessional.isAdmin && <Tag type={TagType.ADMIN} />}

        <Tag type={healthProfessional.status} />
      </div>
    </div>
  );
}
